import { _ } from "../../../../core/polyglot/polyglot.js";
import AbstractColumnTemplate from "./AbstractColumnTemplate.js";
import FormulaColumn from "../../reports/columns/FormulaColumn.js";
import formulaFactory from "../../../keyFigure/formula/formulaFactory.js";
import NaFormulaNode from "../../../keyFigure/formula/NaFormulaNode.js";

/**
 * A column template referencing other column templates through a
 * formula. Such a column can show the sum of 2 other columns for
 * example.
 *
 * @param {AbstractFormulaNode} formula - A tree of formula
 * nodes. Some of those nodes are instances of
 * ReportReferenceFormulaNode to reference columns.
 * @param {boolean} [spec.formatInPercentage] - Whether data should be
 * formatted in percentage.
 */
export default class FormulaColumnTemplate extends AbstractColumnTemplate {
	constructor({ formula, formatInPercentage } = {}) {
		super(...arguments);

		this._formula = formula || new NaFormulaNode();
		this._formatInPercentage = formatInPercentage;
	}

	getFormula() {
		return this._formula;
	}

	setFormula(formula) {
		this._formula = formula;
	}

	getFormatInPercentage() {
		return this._formatInPercentage;
	}

	setFormatInPercentage(formatInPercentage) {
		this._formatInPercentage = formatInPercentage;
	}

	resolveReferences() {
		this._formula.forEach((node) => {
			node.resolveReferences(this._reportTemplate);
		});
	}

	accept(visitor) {
		return visitor.visitFormulaColumnTemplate(this);
	}

	validate() {
		super.validate(...arguments);
		this._formula.validate(...arguments);
	}

	_instanceClass() {
		return FormulaColumn;
	}

	_interpretationSpec() {
		const spec = super._interpretationSpec(...arguments);

		spec.formula = this._formula;
		spec.formatInPercentage = this._formatInPercentage;

		return spec;
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.formula = this._formula;
		properties.isFormattedInPercentage = this._formatInPercentage;

		return properties;
	}

	static getLabel() {
		return _("Formula");
	}

	static getType() {
		return "FormulaColumnTemplate";
	}

	static isVisible() {
		return true;
	}

	static transformMaterializationData(data) {
		super.transformMaterializationData(...arguments);

		data.formula &&= formulaFactory.materialize(data.formula);
		data.formatInPercentage = data.isFormattedInPercentage;
	}
}
